import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { toast } from 'sonner';
import {
  FileSignature,
  Upload,
  Plus,
  Trash2,
  Send,
  Eye,
  Users,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  PenLine,
  Mail,
  MousePointer,
  Copy,
  Search,
  FileWarning,
  Download,
  Archive
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SIGNER_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'
];

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('awaiting');
  const [searchQuery, setSearchQuery] = useState('');

  // Create document flow
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType] = useState('contract');
  const [documentMessage, setDocumentMessage] = useState('');

  // PDF viewer state
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pageSize, setPageSize] = useState({ width: 612, height: 792 });

  // Signature fields
  const [signatureFields, setSignatureFields] = useState([]);
  const [showSignerPopup, setShowSignerPopup] = useState(false);
  const [pendingField, setPendingField] = useState(null);
  const [draggingField, setDraggingField] = useState(null);
  const [resizingField, setResizingField] = useState(null);
  const [newSignerName, setNewSignerName] = useState('');
  const [newSignerEmail, setNewSignerEmail] = useState('');

  // View document
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewPage, setViewPage] = useState(1);
  const [viewNumPages, setViewNumPages] = useState(0);
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [documentSearch, setDocumentSearch] = useState('');

  // Actions
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showSignLinksModal, setShowSignLinksModal] = useState(false);
  const [signLinks, setSignLinks] = useState([]);

  const fileInputRef = useRef(null);
  const pdfContainerRef = useRef(null);

  // Current user role (used for Download/Delete/Archive permission gating).
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('hrms_user') || '{}');
    } catch {
      return {};
    }
  }, []);
  const isHRorAdmin = ['admin', 'hr_manager', 'hr_user', 'platform_admin', 'super_user'].includes(currentUser?.role);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('hrms_token');
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter documents
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    if (activeTab === 'awaiting') {
      filtered = filtered.filter(d => d.status === 'sent' || d.status === 'partially_signed');
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(d => d.status === 'completed');
    } else if (activeTab === 'draft') {
      filtered = filtered.filter(d => d.status === 'draft');
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        d.title?.toLowerCase().includes(query) ||
        d.document_type?.toLowerCase().includes(query) ||
        d.signers?.some(s =>
          s.name?.toLowerCase().includes(query) ||
          s.email?.toLowerCase().includes(query)
        )
      );
    }

    return filtered;
  }, [documents, activeTab, searchQuery]);

  const stats = {
    awaiting: documents.filter(d => d.status === 'sent' || d.status === 'partially_signed').length,
    completed: documents.filter(d => d.status === 'completed').length,
    draft: documents.filter(d => d.status === 'draft').length
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png', 'image/jpeg', 'image/jpg', 'image/gif'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, DOC, DOCX, or image file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('hrms_token');
      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadedFile(response.data);
      setDocumentTitle(file.name.replace(/\.[^/.]+$/, ''));
      setNumPages(response.data.page_count);
      if (response.data.pages?.[0]) {
        setPageSize({
          width: response.data.pages[0].width,
          height: response.data.pages[0].height
        });
      }
      toast.success('Document uploaded! Click to add signature fields.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handlePdfClick = (e) => {
    // Only fires for clicks on EMPTY space (existing field divs use
    // pointer-events: auto + stopPropagation to consume their own events).
    if (!uploadedFile || !pdfContainerRef.current) return;
    if (draggingField || resizingField) return; // suppress during drag

    const rect = pdfContainerRef.current.getBoundingClientRect();
    const rawX = ((e.clientX - rect.left) / rect.width) * 100;
    const rawY = ((e.clientY - rect.top) / rect.height) * 100;
    // Clamp click coords so they always sit inside the page even if the
    // user clicked the padding around the rendered PDF. Subtract half a
    // default field size so the centered field stays fully on-page.
    const defaultW = 20;
    const defaultH = 8;
    const x = Math.max(defaultW / 2, Math.min(100 - defaultW / 2, rawX));
    const y = Math.max(defaultH / 2, Math.min(100 - defaultH / 2, rawY));

    setPendingField({ x, y, page: currentPage });
    setNewSignerName('');
    setNewSignerEmail('');
    setShowSignerPopup(true);
  };

  // ---- Drag-and-drop field placement ---------------------------------
  // Each placed signature field is movable by dragging its body, and
  // resizable via the bottom-right corner handle. All coordinates are
  // clamped to [0, 100]% so the field can never escape the page bounds
  // — fixing the earlier `y=105%` bug that caused signatures to land
  // off-page invisibly.
  const handleFieldMouseDown = (e, fieldId) => {
    e.stopPropagation();
    if (!pdfContainerRef.current) return;
    const rect = pdfContainerRef.current.getBoundingClientRect();
    const field = signatureFields.find((f) => f.id === fieldId);
    if (!field) return;
    // Capture pointer offset from field center so the field doesn't snap
    // its center onto the cursor on the first move event.
    const fieldCenterX = (field.x / 100) * rect.width;
    const fieldCenterY = (field.y / 100) * rect.height;
    setDraggingField({
      id: fieldId,
      offsetX: (e.clientX - rect.left) - fieldCenterX,
      offsetY: (e.clientY - rect.top) - fieldCenterY,
    });
  };

  const handleResizeMouseDown = (e, fieldId) => {
    e.stopPropagation();
    setResizingField({ id: fieldId });
  };

  // Global mousemove / mouseup handlers wired via useEffect below.
  const handleContainerMouseMove = (e) => {
    if (!pdfContainerRef.current) return;
    const rect = pdfContainerRef.current.getBoundingClientRect();
    if (draggingField) {
      const dragX = ((e.clientX - rect.left - draggingField.offsetX) / rect.width) * 100;
      const dragY = ((e.clientY - rect.top - draggingField.offsetY) / rect.height) * 100;
      setSignatureFields((prev) => prev.map((f) => {
        if (f.id !== draggingField.id) return f;
        // Clamp so the rect stays fully on-page (account for centered origin)
        const halfW = f.width / 2;
        const halfH = f.height / 2;
        const x = Math.max(halfW, Math.min(100 - halfW, dragX));
        const y = Math.max(halfH, Math.min(100 - halfH, dragY));
        return { ...f, x, y };
      }));
    } else if (resizingField) {
      setSignatureFields((prev) => prev.map((f) => {
        if (f.id !== resizingField.id) return f;
        // The bottom-right corner follows the cursor; resize means the
        // distance from the center to the corner = (cursor% - center%).
        const cursorXp = ((e.clientX - rect.left) / rect.width) * 100;
        const cursorYp = ((e.clientY - rect.top) / rect.height) * 100;
        const newHalfW = Math.max(2.5, cursorXp - f.x); // min 5% wide
        const newHalfH = Math.max(2, cursorYp - f.y);   // min 4% tall
        const newW = Math.min(60, newHalfW * 2);
        const newH = Math.min(30, newHalfH * 2);
        // Clamp so it stays on-page
        const halfW = newW / 2;
        const halfH = newH / 2;
        const x = Math.max(halfW, Math.min(100 - halfW, f.x));
        const y = Math.max(halfH, Math.min(100 - halfH, f.y));
        return { ...f, width: newW, height: newH, x, y };
      }));
    }
  };

  const handleContainerMouseUp = () => {
    if (draggingField) setDraggingField(null);
    if (resizingField) setResizingField(null);
  };

  const handleAddSignatureField = () => {
    // Iter47 (Shikha 22-Jun-2026) — surface specific validation per field so
    // users know exactly what's missing instead of a confusing combined message
    // when they've already filled one of the two inputs.
    const nameOk = !!newSignerName.trim();
    const emailOk = !!newSignerEmail.trim();
    if (!nameOk && !emailOk) {
      toast.error('Please enter signer name and email');
      return;
    }
    if (!nameOk) {
      toast.error('Please enter signer name');
      return;
    }
    if (!emailOk) {
      toast.error('Please enter signer email');
      return;
    }

    if (!newSignerEmail.includes('@') || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newSignerEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    const colorIndex = signatureFields.length % SIGNER_COLORS.length;

    // Field is stored with x/y at its CENTER, percentages of page.
    // Clamp so the rect stays inside the page even if pendingField
    // came from an edge-case click.
    const w = 20;
    const h = 8;
    const x = Math.max(w / 2, Math.min(100 - w / 2, pendingField.x));
    const y = Math.max(h / 2, Math.min(100 - h / 2, pendingField.y));
    const newField = {
      id: `field-${Date.now()}`,
      signerName: newSignerName.trim(),
      signerEmail: newSignerEmail.trim(),
      color: SIGNER_COLORS[colorIndex],
      page: pendingField.page,
      x,
      y,
      width: w,
      height: h,
    };

    setSignatureFields([...signatureFields, newField]);
    setShowSignerPopup(false);
    setPendingField(null);
    toast.success(`Added signature field for ${newSignerName}`);
  };

  const removeSignatureField = (fieldId) => {
    setSignatureFields(signatureFields.filter(f => f.id !== fieldId));
  };

  const handleCreateDocument = async (sendImmediately = false) => {
    if (!uploadedFile || !documentTitle.trim()) {
      toast.error('Please upload a document and enter a title');
      return;
    }

    if (signatureFields.length === 0) {
      toast.error('Please add at least one signature field');
      return;
    }

    setCreating(true);

    try {
      const token = localStorage.getItem('hrms_token');

      const signerMap = {};
      signatureFields.forEach(field => {
        if (!signerMap[field.signerEmail]) {
          signerMap[field.signerEmail] = {
            name: field.signerName,
            email: field.signerEmail,
            color: field.color,
            signature_fields: []
          };
        }
        signerMap[field.signerEmail].signature_fields.push({
          page: field.page,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height
        });
      });

      const signersData = Object.values(signerMap);

      const formData = new FormData();
      formData.append('title', documentTitle);
      formData.append('document_type', documentType);
      formData.append('message', documentMessage);
      formData.append('file_id', uploadedFile.file_id);
      formData.append('pdf_filename', uploadedFile.pdf_filename);
      formData.append('signers', JSON.stringify(signersData));

      const createResponse = await axios.post(`${API}/documents`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const documentId = createResponse.data.document_id;

      if (sendImmediately) {
        const sendResponse = await axios.post(
          `${API}/documents/${documentId}/send`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const links = sendResponse.data.email_results || [];
        setSignLinks(links);
        setShowSignLinksModal(true);
        toast.success(`Document sent to ${links.length} signer(s)!`);
      } else {
        toast.success('Document saved as draft');
      }

      resetCreateModal();
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create document');
    } finally {
      setCreating(false);
    }
  };

  const handleSendDocument = async (documentId) => {
    setSending(true);
    try {
      const token = localStorage.getItem('hrms_token');
      const response = await axios.post(
        `${API}/documents/${documentId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data || {};
      const links = data.email_results || [];
      setSignLinks(links);
      setShowSignLinksModal(true);

      // Surface real delivery status — never silently claim "sent" if the
      // backend reported the email service is disabled / partially failed.
      const summaryStatus = data.summary_status;
      const summaryMsg = data.summary_message || 'Signature request created';
      if (summaryStatus === 'sent') {
        toast.success(summaryMsg);
      } else if (summaryStatus === 'disabled') {
        toast.warning(summaryMsg, { duration: 6000 });
      } else if (summaryStatus === 'partial') {
        toast.warning(summaryMsg, { duration: 6000 });
      } else if (summaryStatus === 'failed') {
        toast.error(summaryMsg, { duration: 6000 });
      } else {
        toast.success(summaryMsg);
      }

      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteDocument = async (documentId, status) => {
    // The backend hard-deletes draft/expired/declined, soft-archives the
    // "awaiting signature" statuses (sent / partially_signed / awaiting),
    // and blocks completed documents (retention rule).
    const willArchive = ['sent', 'partially_signed', 'awaiting'].includes(status);
    const promptText = willArchive
      ? 'Archive this document? The signature request will be cancelled and it will no longer appear in the active list.'
      : 'Delete this document? This cannot be undone.';
    if (!window.confirm(promptText)) return;

    setDeletingId(documentId);
    try {
      const token = localStorage.getItem('hrms_token');
      const res = await axios.delete(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const action = res.data?.action || (willArchive ? 'archived' : 'deleted');
      toast.success(action === 'archived' ? 'Document archived' : 'Document deleted');
      if (showViewModal) {
        setShowViewModal(false);
      }
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  // Authenticated download of the underlying PDF (signed if available, else original).
  // Falls back to fetching the file URL with the Bearer token so the file_url that
  // would otherwise 401/403 for unauthenticated clients still streams correctly.
  const handleDownloadDocument = async (doc) => {
    if (!doc) return;
    const pdfPath = doc.signed_pdf_url || doc.file_url;
    if (!pdfPath) {
      toast.error('No file is attached to this document');
      return;
    }
    setDownloadingId(doc.document_id);
    try {
      const token = localStorage.getItem('hrms_token');
      const url = `${process.env.REACT_APP_BACKEND_URL}${pdfPath}`;
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = blobUrl;
      const safeTitle = (doc.title || 'document').replace(/[^a-z0-9_\-]/gi, '_');
      a.download = `${safeTitle}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Download started');
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('You are not authorized to download this document');
      } else {
        toast.error(err.response?.data?.detail || 'Download failed');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const viewDocument = async (doc) => {
    try {
      const token = localStorage.getItem('hrms_token');
      const response = await axios.get(`${API}/documents/${doc.document_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDocument(response.data);
      setViewPage(1);
      setViewNumPages(0);
      setPdfError(false);
      setPdfLoading(true);
      setDocumentSearch('');
      setShowViewModal(true);
    } catch (err) {
      toast.error('Failed to load document');
    }
  };

  const resetCreateModal = () => {
    setShowCreateModal(false);
    setUploadedFile(null);
    setDocumentTitle('');
    setDocumentType('contract');
    setDocumentMessage('');
    setSignatureFields([]);
    setCurrentPage(1);
    setNumPages(0);
    setPendingField(null);
    setShowSignerPopup(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied!');
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page) => {
    setPageSize({ width: page.width, height: page.height });
  };

  // Get the correct PDF URL for viewing
  const getViewPdfUrl = () => {
    if (!selectedDocument) return null;
    
    // Use signed PDF if available
    if (selectedDocument.signed_pdf_url) {
      return `${process.env.REACT_APP_BACKEND_URL}${selectedDocument.signed_pdf_url}`;
    }
    
    // Use original PDF
    if (selectedDocument.file_url) {
      return `${process.env.REACT_APP_BACKEND_URL}${selectedDocument.file_url}`;
    }
    
    return null;
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-slate-500', icon: FileText, label: 'Draft' },
      sent: { color: 'bg-blue-500', icon: Mail, label: 'Awaiting' },
      partially_signed: { color: 'bg-amber-500', icon: Clock, label: 'Partial' },
      completed: { color: 'bg-emerald-500', icon: CheckCircle2, label: 'Completed' }
    };
    const c = config[status] || config.draft;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white ${c.color}`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </span>
    );
  };

  const displayWidth = 650;

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <FileSignature className="h-7 w-7 text-emerald-500" />
            Digital Signatures
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Upload documents and collect e-signatures
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="new-document-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Document
        </Button>
      </div>

      {/* Tabs + Search */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
          {[
            { id: 'awaiting', label: 'Awaiting Signature', icon: Clock, count: stats.awaiting },
            { id: 'completed', label: 'Completed', icon: CheckCircle2, count: stats.completed },
            { id: 'draft', label: 'Drafts', icon: FileText, count: stats.draft }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-4 px-6 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <tab.icon className="h-4 w-4" />
                {tab.label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' : 'bg-slate-100 dark:bg-slate-700'
                }`}>
                  {tab.count}
                </span>
              </div>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search documents, signers, or document type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-500" />
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="p-12 text-center">
            <FileSignature className="h-16 w-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-lg text-slate-500 dark:text-slate-400">
              {searchQuery ? 'No documents match your search' : `No ${activeTab === 'awaiting' ? 'pending' : activeTab} documents`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {filteredDocuments.map(doc => (
              <div
                key={doc.document_id}
                className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                onClick={() => viewDocument(doc)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-slate-900 dark:text-white truncate">
                        {doc.title}
                      </h3>
                      {getStatusBadge(doc.status)}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {doc.signers?.length || 0} signer(s)
                      </span>
                      <span>
                        {doc.signers?.filter(s => s.status === 'signed').length || 0} / {doc.signers?.length || 0} signed
                      </span>
                      <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {doc.signers?.slice(0, 3).map((s, idx) => (
                        <span
                          key={idx}
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            s.status === 'signed'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {s.name} {s.status === 'signed' && '✓'}
                        </span>
                      ))}
                      {(doc.signers?.length || 0) > 3 && (
                        <span className="text-xs text-slate-400">+{doc.signers.length - 3} more</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4" onClick={e => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewDocument(doc)}
                      data-testid={`view-document-${doc.document_id}`}
                      title="View"
                      className="border-slate-300 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadDocument(doc)}
                      disabled={downloadingId === doc.document_id || (!doc.signed_pdf_url && !doc.file_url)}
                      data-testid={`download-document-${doc.document_id}`}
                      title={(!doc.signed_pdf_url && !doc.file_url) ? 'No file attached' : 'Download PDF'}
                      className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-200 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40 disabled:opacity-50"
                    >
                      {downloadingId === doc.document_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    </Button>
                    {doc.status === 'draft' && isHRorAdmin && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleSendDocument(doc.document_id)}
                          disabled={sending}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Send
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.document_id, doc.status)}
                          disabled={deletingId === doc.document_id}
                          data-testid={`delete-document-${doc.document_id}`}
                          title="Delete draft"
                          className="border-red-400 text-red-700 hover:bg-red-50 dark:border-red-500 dark:text-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/40 disabled:opacity-50"
                        >
                          {deletingId === doc.document_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </>
                    )}
                    {(doc.status === 'sent' || doc.status === 'partially_signed' || doc.status === 'awaiting') && isHRorAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.document_id, doc.status)}
                        disabled={deletingId === doc.document_id}
                        data-testid={`archive-document-${doc.document_id}`}
                        title="Archive (cancels the signature request)"
                        className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-500 dark:text-amber-200 dark:bg-amber-900/20 dark:hover:bg-amber-900/40 disabled:opacity-50"
                      >
                        {deletingId === doc.document_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                      </Button>
                    )}
                    {(doc.status === 'expired' || doc.status === 'declined') && isHRorAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.document_id, doc.status)}
                        disabled={deletingId === doc.document_id}
                        data-testid={`delete-document-${doc.document_id}`}
                        title="Delete"
                        className="border-red-400 text-red-700 hover:bg-red-50 dark:border-red-500 dark:text-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/40 disabled:opacity-50"
                      >
                        {deletingId === doc.document_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CREATE DOCUMENT MODAL */}
      <Dialog open={showCreateModal} onOpenChange={(open) => !open && resetCreateModal()}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-4 border-b border-slate-200 dark:border-slate-700">
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-emerald-500" />
              {!uploadedFile ? 'Upload Document' : 'Place Signature Fields'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex">
            {!uploadedFile && (
              <div className="flex-1 p-8 flex items-center justify-center">
                <div
                  className="w-full max-w-xl border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-2xl p-12 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-all"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <div>
                      <Loader2 className="h-16 w-16 animate-spin mx-auto text-emerald-500" />
                      <p className="mt-4 text-lg text-slate-600">Uploading...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-16 w-16 mx-auto text-slate-400" />
                      <p className="mt-4 text-xl font-medium text-slate-700 dark:text-slate-300">
                        Drop your document here
                      </p>
                      <p className="text-slate-500 mt-2">or click to browse</p>
                      <p className="text-sm text-slate-400 mt-4">PDF, DOC, DOCX, PNG, JPG</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            )}

            {uploadedFile && (
              <>
                <div className="w-72 border-r border-slate-200 dark:border-slate-700 p-4 overflow-auto bg-slate-50 dark:bg-slate-900">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs text-slate-500">Title</Label>
                      <Input value={documentTitle} onChange={(e) => setDocumentTitle(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Type</Label>
                      <Select value={documentType} onValueChange={setDocumentType}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="offer_letter">Offer Letter</SelectItem>
                          <SelectItem value="nda">NDA</SelectItem>
                          <SelectItem value="sow">SOW</SelectItem>
                          <SelectItem value="agreement">Agreement</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Message</Label>
                      <Input value={documentMessage} onChange={(e) => setDocumentMessage(e.target.value)} className="mt-1" />
                    </div>
                    <div className="pt-4 border-t">
                      <div className="flex justify-between mb-3">
                        <h4 className="font-semibold text-sm">Signature Fields</h4>
                        <span className="text-xs text-slate-500">{signatureFields.length}</span>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                        <p className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                          <MousePointer className="h-4 w-4 mt-0.5" />
                          Click on document to add signatures
                        </p>
                      </div>
                      {signatureFields.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">No fields yet</p>
                      ) : (
                        <div className="space-y-2">
                          {signatureFields.map((field, idx) => (
                            <div key={field.id} className="flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-slate-800 border">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs" style={{ backgroundColor: field.color }}>{idx + 1}</div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{field.signerName}</p>
                                <p className="text-xs text-slate-500 truncate">{field.signerEmail}</p>
                              </div>
                              <button onClick={() => removeSignatureField(field.id)} className="text-red-500 p-1"><X className="h-4 w-4" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex flex-col bg-slate-100 dark:bg-slate-900">
                  <div className="flex items-center justify-between gap-2 p-3 border-b bg-white dark:bg-slate-800">
                    <div className="text-xs text-slate-500 dark:text-slate-400 hidden sm:flex items-center gap-1">
                      <MousePointer className="h-3 w-3" />
                      Click empty space to place • Drag a field to move • Drag the corner to resize
                    </div>
                    <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">Page {currentPage} / {numPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto p-4 flex justify-center">
                    <div
                      ref={pdfContainerRef}
                      className={`relative bg-white shadow-xl ${(draggingField || resizingField) ? 'cursor-grabbing select-none' : 'cursor-crosshair'}`}
                      onClick={handlePdfClick}
                      onMouseMove={handleContainerMouseMove}
                      onMouseUp={handleContainerMouseUp}
                      onMouseLeave={handleContainerMouseUp}
                      data-testid="pdf-placement-canvas"
                    >
                      <Document file={`${process.env.REACT_APP_BACKEND_URL}${uploadedFile.file_url}`} onLoadSuccess={onDocumentLoadSuccess} loading={<Loader2 className="h-8 w-8 animate-spin mx-auto m-8" />}>
                        <Page pageNumber={currentPage} onLoadSuccess={onPageLoadSuccess} width={displayWidth} renderTextLayer={false} renderAnnotationLayer={false} />
                      </Document>
                      {signatureFields.filter(f => f.page === currentPage).map((field) => (
                        <div
                          key={field.id}
                          className="absolute border-2 rounded flex items-center justify-center group cursor-grab hover:shadow-md"
                          style={{
                            left: `${field.x - field.width/2}%`,
                            top: `${field.y - field.height/2}%`,
                            width: `${field.width}%`,
                            height: `${field.height}%`,
                            borderColor: field.color,
                            backgroundColor: `${field.color}30`,
                            pointerEvents: 'auto',
                            userSelect: 'none',
                          }}
                          onMouseDown={(e) => handleFieldMouseDown(e, field.id)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`placed-field-${field.id}`}
                          title="Drag to reposition • Drag corner to resize"
                        >
                          <span
                            className="text-xs font-medium truncate px-1 pointer-events-none"
                            style={{ color: field.color }}
                          >
                            {field.signerName}
                          </span>
                          {/* Delete (×) button */}
                          <button
                            type="button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSignatureFields((prev) => prev.filter((f) => f.id !== field.id));
                            }}
                            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-white text-slate-700 border border-slate-300 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition"
                            data-testid={`remove-field-${field.id}`}
                            title="Remove field"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          {/* Resize handle (bottom-right corner) */}
                          <span
                            role="button"
                            tabIndex={-1}
                            onMouseDown={(e) => handleResizeMouseDown(e, field.id)}
                            className="absolute -bottom-1.5 -right-1.5 h-3 w-3 rounded-sm bg-white border-2 cursor-nwse-resize shadow opacity-0 group-hover:opacity-100 transition"
                            style={{ borderColor: field.color }}
                            data-testid={`resize-field-${field.id}`}
                            title="Drag to resize"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {uploadedFile && (
            <DialogFooter className="p-4 border-t bg-white dark:bg-slate-800">
              <Button variant="outline" onClick={resetCreateModal}>Cancel</Button>
              <Button onClick={() => handleCreateDocument(false)} disabled={creating || signatureFields.length === 0} variant="outline">Save Draft</Button>
              <Button onClick={() => handleCreateDocument(true)} disabled={creating || signatureFields.length === 0} className="bg-emerald-600 hover:bg-emerald-700">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* SIGNER POPUP */}
      <Dialog open={showSignerPopup} onOpenChange={setShowSignerPopup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle><PenLine className="h-5 w-5 text-emerald-500 inline mr-2" />Add Signer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Signer Name</Label>
              <Input value={newSignerName} onChange={(e) => setNewSignerName(e.target.value)} placeholder="John Smith" autoFocus />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={newSignerEmail} onChange={(e) => setNewSignerEmail(e.target.value)} placeholder="john@company.com" onKeyDown={(e) => e.key === 'Enter' && handleAddSignatureField()} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignerPopup(false)}>Cancel</Button>
            <Button onClick={handleAddSignatureField} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4 mr-2" />Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIGN LINKS MODAL */}
      <Dialog open={showSignLinksModal} onOpenChange={setShowSignLinksModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle><CheckCircle2 className="h-5 w-5 text-emerald-500 inline mr-2" />Document Sent!</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">Share these signing links:</p>
            <div className="space-y-3">
              {signLinks.map((link, idx) => {
                const isSent = link.status === 'sent';
                const isMocked = link.status === 'email_not_configured';
                const isFailed = link.status === 'failed';
                const badge = isSent
                  ? { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', text: 'Email sent' }
                  : isMocked
                    ? { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', text: 'Email not configured' }
                    : isFailed
                      ? { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', text: 'Email failed' }
                      : { cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200', text: 'Share' };
                return (
                  <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg" data-testid={`sign-link-row-${idx}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-medium">{link.name}</span>
                        <p className="text-sm text-slate-500">{link.email}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${badge.cls}`} data-testid={`sign-link-email-status-${idx}`}>
                          {badge.text}
                        </span>
                        {link.in_app_notified && (
                          <span className="text-[10px] text-emerald-600 dark:text-emerald-400" title="An in-app notification was created for this user">
                            ✓ In-app notified
                          </span>
                        )}
                      </div>
                    </div>
                    {(isMocked || isFailed) && (
                      <p className="text-xs text-amber-700 dark:text-amber-300 mb-2 italic">
                        {isMocked
                          ? 'Email service is not configured. Share the link below manually — the signer will also see the request in their MyHR Orbit app.'
                          : `Email delivery failed${link.reason ? `: ${link.reason}` : ''}. Share the link below manually — the signer will also see the request in-app.`}
                      </p>
                    )}
                    {link.sign_url && (
                      <div className="flex gap-2">
                        <Input value={link.sign_url} readOnly className="text-xs" data-testid={`sign-link-url-${idx}`} />
                        <Button size="sm" variant="outline" onClick={() => copyToClipboard(link.sign_url)} data-testid={`copy-sign-link-${idx}`}><Copy className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSignLinksModal(false)} className="bg-emerald-600 hover:bg-emerald-700">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW DOCUMENT MODAL */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-4 border-b border-slate-200 dark:border-slate-700">
            <DialogTitle className="flex items-center gap-3">
              {selectedDocument?.title}
              {selectedDocument && getStatusBadge(selectedDocument.status)}
            </DialogTitle>
          </DialogHeader>

          {selectedDocument && (
            <div className="flex-1 overflow-hidden flex">
              {/* PDF Preview */}
              <div className="flex-1 flex flex-col bg-slate-100 dark:bg-slate-900">
                {/* Page nav + search */}
                <div className="flex items-center justify-between gap-4 p-3 border-b bg-white dark:bg-slate-800">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setViewPage(Math.max(1, viewPage - 1))} disabled={viewPage <= 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">Page {viewPage} / {viewNumPages || '?'}</span>
                    <Button variant="outline" size="sm" onClick={() => setViewPage(Math.min(viewNumPages, viewPage + 1))} disabled={viewPage >= viewNumPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search in document..."
                      value={documentSearch}
                      onChange={(e) => setDocumentSearch(e.target.value)}
                      className="pl-10 h-8 text-sm"
                    />
                  </div>
                </div>

                {/* PDF viewer */}
                <div className="flex-1 overflow-auto p-4 flex justify-center">
                  {getViewPdfUrl() ? (
                    <div className="bg-white shadow-xl">
                      <Document
                        file={getViewPdfUrl()}
                        onLoadSuccess={({ numPages }) => {
                          setViewNumPages(numPages);
                          setPdfLoading(false);
                          setPdfError(false);
                        }}
                        onLoadError={(error) => {
                          console.error('PDF load error:', error);
                          setPdfError(true);
                          setPdfLoading(false);
                        }}
                        loading={
                          <div className="p-12 text-center">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-500" />
                            <p className="mt-2 text-slate-500">Loading document...</p>
                          </div>
                        }
                        error={
                          <div className="p-12 text-center">
                            <FileWarning className="h-12 w-12 mx-auto text-amber-500 mb-4" />
                            <p className="text-lg font-medium text-slate-700 dark:text-slate-300">Document Unavailable</p>
                            <p className="text-sm text-slate-500 mt-2">
                              The original document file could not be loaded.
                            </p>
                            <p className="text-xs text-slate-400 mt-4">
                              This may be a legacy document created before the file upload feature.
                            </p>
                          </div>
                        }
                      >
                        <Page
                          pageNumber={viewPage}
                          width={550}
                          renderTextLayer={!!documentSearch}
                          renderAnnotationLayer={false}
                        />
                      </Document>
                    </div>
                  ) : (
                    <div className="p-12 text-center bg-white rounded-lg shadow">
                      <FileWarning className="h-12 w-12 mx-auto text-amber-500 mb-4" />
                      <p className="text-lg font-medium text-slate-700">No Document File</p>
                      <p className="text-sm text-slate-500 mt-2">
                        This document does not have an associated file.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Signers Sidebar */}
              <div className="w-72 border-l border-slate-200 dark:border-slate-700 p-4 overflow-auto">
                <h3 className="font-semibold mb-4">Signers</h3>
                <div className="space-y-3">
                  {selectedDocument.signers?.map((signer, idx) => (
                    <div key={signer.signer_id} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: signer.color || SIGNER_COLORS[idx % SIGNER_COLORS.length] }}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{signer.name}</p>
                          <p className="text-xs text-slate-500 truncate">{signer.email}</p>
                        </div>
                      </div>
                      <div className="mt-2">
                        {signer.status === 'signed' ? (
                          <span className="text-xs text-emerald-600 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Signed {signer.signed_at && new Date(signer.signed_at).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Pending
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t">
                  <p className="text-xs text-slate-500">Created</p>
                  <p className="text-sm">{new Date(selectedDocument.created_at).toLocaleDateString()}</p>
                  {selectedDocument.completed_at && (
                    <>
                      <p className="text-xs text-slate-500 mt-2">Completed</p>
                      <p className="text-sm">{new Date(selectedDocument.completed_at).toLocaleDateString()}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer actions: visible Download (all roles) + Delete/Archive (HR/Admin only).
              The previous viewer only let users close — testers couldn't download or
              cancel a request from the open dialog, so HR had to reload + scroll the
              list to act on the document. */}
          {selectedDocument && (
            <DialogFooter
              className="px-6 py-3 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
              data-testid="document-viewer-footer"
            >
              <Button
                variant="outline"
                onClick={() => setShowViewModal(false)}
                className="border-slate-300 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700"
                data-testid="viewer-close-btn"
              >
                Close
              </Button>
              <Button
                type="button"
                onClick={() => handleDownloadDocument(selectedDocument)}
                disabled={downloadingId === selectedDocument.document_id || (!selectedDocument.signed_pdf_url && !selectedDocument.file_url)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                data-testid="viewer-download-btn"
                title={(!selectedDocument.signed_pdf_url && !selectedDocument.file_url) ? 'No file attached' : 'Download PDF'}
              >
                {downloadingId === selectedDocument.document_id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download
              </Button>
              {isHRorAdmin && ['sent', 'partially_signed', 'awaiting'].includes(selectedDocument.status) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDeleteDocument(selectedDocument.document_id, selectedDocument.status)}
                  disabled={deletingId === selectedDocument.document_id}
                  data-testid="viewer-archive-btn"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-500 dark:text-amber-200 dark:bg-amber-900/20 dark:hover:bg-amber-900/40"
                  title="Archive — cancels the signature request"
                >
                  {deletingId === selectedDocument.document_id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Archive className="h-4 w-4 mr-2" />
                  )}
                  Archive
                </Button>
              )}
              {isHRorAdmin && ['draft', 'expired', 'declined'].includes(selectedDocument.status) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDeleteDocument(selectedDocument.document_id, selectedDocument.status)}
                  disabled={deletingId === selectedDocument.document_id}
                  data-testid="viewer-delete-btn"
                  className="border-red-400 text-red-700 hover:bg-red-50 dark:border-red-500 dark:text-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/40"
                >
                  {deletingId === selectedDocument.document_id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete
                </Button>
              )}
              {isHRorAdmin && selectedDocument.status === 'completed' && (
                <span
                  className="inline-flex items-center text-xs px-3 py-2 rounded-md bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                  data-testid="viewer-retention-notice"
                  title="Completed signed documents must be retained for audit"
                >
                  <AlertCircle className="h-3 w-3 mr-1.5" />
                  Retention rule — cannot be deleted
                </span>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
